// 台灣的國定假日(業主 2026-10-15:日曆上國定假日含六日的數字要紅字)。
//
// 資料由後端 `GET /api/holidays?year=` 給(它向人事行政總處的辦公日曆拉,拉不到有備援,
// 見 `backend/app/services/tw_holidays.py`)。這裡每一年只問一次,記在模組層 ——
// 首頁日曆與打卡機日曆翻月時不必每次都問。
//
// 還沒拿到(或拿不到)的那一年先只標六日:日曆不可以因為假日資料慢一步就畫不出來。

import { useEffect, useState } from "react";
import { api } from "../api/client";

interface HolidayYear {
  year: number;
  source: string;
  off: string[];
  names: Record<string, string>;
}

export interface Holidays {
  /** 放假日(YYYY-MM-DD → 節日名稱,沒有名稱的六日是空字串)。 */
  off: Map<string, string>;
  /** 已經拿到資料的年份;沒拿到的那幾年退回只看六日。 */
  years: Set<number>;
}

const pending = new Map<number, Promise<HolidayYear | null>>();

function load(year: number): Promise<HolidayYear | null> {
  let got = pending.get(year);
  if (!got) {
    got = api.get<HolidayYear>("/holidays", { year }).catch(() => {
      pending.delete(year);  // 失敗的下次再試
      return null;
    });
    pending.set(year, got);
  }
  return got;
}

/** 測試用:把記住的答案丟掉。 */
export function forgetHolidays(): void {
  pending.clear();
}

/** 這幾年的放假日。年份變了才重問(同一年記在模組層,只問一次)。 */
export function useTwHolidays(years: number[]): Holidays {
  const key = [...new Set(years)].sort().join(",");
  const [state, setState] = useState<Holidays>({ off: new Map(), years: new Set() });
  useEffect(() => {
    let alive = true;
    const wanted = key.split(",").filter(Boolean).map(Number);
    void Promise.all(wanted.map(load)).then((rows) => {
      if (!alive) return;
      const off = new Map<string, string>();
      const got = new Set<number>();
      for (const row of rows) {
        if (!row || !Array.isArray(row.off)) continue;
        got.add(row.year);
        for (const iso of row.off) off.set(iso, row.names?.[iso] ?? "");
      }
      setState({ off, years: got });
    });
    return () => {
      alive = false;
    };
  }, [key]);
  return state;
}

/** 這一天放不放假。那一年的資料還沒到就只看六日。 */
export function isHoliday(holidays: Holidays, iso: string): boolean {
  const year = Number(iso.slice(0, 4));
  if (holidays.years.has(year)) return holidays.off.has(iso);
  const [y, m, d] = iso.split("-").map(Number);
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return day === 0 || day === 6;
}

/** 節日名稱(不是國定假日就是空字串)。 */
export function holidayName(holidays: Holidays, iso: string): string {
  return holidays.off.get(iso) ?? "";
}
